import React from "react";
import { useField } from "react-final-form";
import { ToolTip } from "@truevault/aquilo";

// Same look as aquilo's RadioGroup inputs, usable on its own (table cells, option cards).
export const RADIO_CLASS =
  "relative m-0 h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-full border border-solid border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-primary-600 checked:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 [&:not(:checked)]:before:hidden";

interface ConsentRadioControlledProps {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
  // Shown on hover when the radio is disabled.
  disabledReason?: string;
}

export const ConsentRadioControlled = ({
  name,
  checked,
  onChange,
  label,
  disabled,
  disabledReason,
}: ConsentRadioControlledProps) => {
  const radio = (
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onChange}
      aria-label={label}
      disabled={disabled}
      className={RADIO_CLASS}
    />
  );

  return disabled && disabledReason ? (
    <ToolTip inline text={disabledReason} maxWidth="320px">
      <span className="inline-flex">{radio}</span>
    </ToolTip>
  ) : (
    radio
  );
};

interface ConsentRadioProps {
  field: string;
  value: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
}

/** A single radio wired to a react-final-form field. */
export const ConsentRadio = ({
  field,
  value,
  label,
  disabled,
  disabledReason,
}: ConsentRadioProps) => {
  const { input } = useField(field, { type: "radio", value });

  return (
    <ConsentRadioControlled
      name={input.name}
      checked={Boolean(input.checked)}
      onChange={() => input.onChange(value)}
      label={label}
      disabled={disabled}
      disabledReason={disabledReason}
    />
  );
};
